// Extracted from original/src/components/WatchdogLogsModal.tsx:41-825 ("نظام الحراسة المستقل وسجل العمليات والأخطاء",
// ADR-08 §2.1 "copy, then cut logic"; D-136, D-137; chat C34):
//   frame, header with the title / subtitle / version chip / status pill / close             :229-286
//   health strip (six tiles)                                                                 :288-345
//   tab bar                                                                                  :347-399
//   tab 1 entry stream: filter bar (search, level, module, live, refresh), entry cards,
//   expandable details                                                                       :401-628
//   tab 2 diagnostics: probe card + result cards                                             :630-708
// Kept: every class string of the frame, header, pill, strip tiles, tabs, filter bar, entry cards (level colours,
// badges, expand line) and the diagnostics cards; icons and sizes; the slate dark look in both themes (the old window
// was always dark).
// Cut / changed by decision:
//   * the window shows the **server** watchdog (D-136), not the browser's: the client heartbeat, memory, storage probe
//     and client log stream (watchdogService.ts, loggerService.ts; R-A9, R-A10) are gone; the strip shows the last run
//     of each server check and the open critical / warning counts; the entries are the watchdog's findings (level =
//     severity, module = the check), their details the finding's record;
//   * no client / server source toggle (one source), no "clear" button (users cannot clear logs, D-137), no log-folder
//     tab and no downloads (no user-chosen server path, D-137, T-40; the office's record is Audit & Control, ADR-04
//     §2.6) — the third tab is removed;
//   * the diagnostics tab lists the server checks with their last result instead of running a browser probe; its
//     button refreshes (the watchdog runs on its own schedule, ADR-04 §2.6);
//   * opened only with `view_logs_watchdog` (D-136; old: everyone, R-A9) — the container decides.
// Added: data-testid attributes.
import { Activity, AlertCircle, AlertTriangle, CheckCircle2, Clock, Pause, Play, RefreshCw, Search, ShieldAlert, Terminal, X, Zap } from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '../i18n-context.js';

export type WatchdogHealth = 'healthy' | 'warning' | 'critical' | 'unknown';
export type WatchdogSeverity = 'critical' | 'warning' | 'info';
export type WatchdogTab = 'stream' | 'diagnostics';

export interface WatchdogTile {
  readonly labelKey: string;
  readonly value: string;
  readonly tone: 'ok' | 'warn' | 'bad' | 'muted';
}

export interface WatchdogCheckRow {
  readonly name: string;
  readonly labelKey: string;
  readonly lastRun: string | null;
  readonly outcome: 'clean' | 'findings' | 'error' | null;
  readonly findings: number;
  readonly error: string | null;
}

export interface WatchdogEntry {
  readonly id: string;
  readonly severity: WatchdogSeverity;
  readonly check: string;
  readonly checkLabelKey: string;
  readonly time: string;
  readonly message: string;
  readonly details: string;
  readonly cleared: boolean;
}

export interface WatchdogWindowProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly status: WatchdogHealth;
  readonly tiles: readonly WatchdogTile[];
  readonly tab: WatchdogTab;
  readonly onTab: (t: WatchdogTab) => void;
  readonly entries: readonly WatchdogEntry[];
  readonly checks: readonly WatchdogCheckRow[];
  readonly search: string;
  readonly onSearch: (v: string) => void;
  readonly level: 'ALL' | WatchdogSeverity;
  readonly onLevel: (v: 'ALL' | WatchdogSeverity) => void;
  readonly module: string;
  readonly onModule: (v: string) => void;
  readonly live: boolean;
  readonly onToggleLive: () => void;
  readonly refreshing: boolean;
  readonly onRefresh: () => void;
  readonly showCleared: boolean;
  readonly onToggleCleared: () => void;
}

const TONE: Record<WatchdogTile['tone'], string> = { ok: 'text-emerald-400', warn: 'text-amber-400', bad: 'text-rose-400', muted: 'text-slate-500' };

export function WatchdogWindow(p: WatchdogWindowProps) {
  const { t, locale } = useI18n();
  const [expanded, setExpanded] = useState<string | null>(null);
  if (!p.open) return null;
  const filtered = p.entries.filter((e) => {
    if (p.level !== 'ALL' && e.severity !== p.level) return false;
    if (p.module !== 'ALL' && e.check !== p.module) return false;
    if (p.search.trim()) {
      const q = p.search.toLowerCase();
      if (!e.message.toLowerCase().includes(q) && !e.details.toLowerCase().includes(q) && !e.check.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const modules = [...new Set(p.entries.map((e) => e.check))].sort();
  const levelBadge = (s: WatchdogSeverity) =>
    s === 'critical' ? (
      <span className="px-2 py-0.5 text-[10px] font-black rounded-md bg-red-600 text-white animate-pulse">CRITICAL</span>
    ) : s === 'warning' ? (
      <span className="px-2 py-0.5 text-[10px] font-black rounded-md bg-amber-500/20 border border-amber-500/30 text-amber-400">WARN</span>
    ) : (
      <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-emerald-500/20 border border-emerald-500/30 text-emerald-400">INFO</span>
    );
  const tileIcon = (i: number, tone: WatchdogTile['tone']) => {
    const cls = TONE[tone];
    switch (i) {
      case 0:
        return <ShieldAlert size={14} className={cls} />;
      case 1:
        return <Activity size={14} className={cls} />;
      case 2:
        return <Clock size={14} className={cls} />;
      case 3:
        return <Zap size={14} className={cls} />;
      case 4:
        return <AlertCircle size={14} className={cls} />;
      default:
        return <AlertTriangle size={14} className={cls} />;
    }
  };
  return (
    <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 dir-rtl" dir={locale === 'ar' ? 'rtl' : 'ltr'} data-testid="watchdog-window">
      <div className="bg-slate-900 border border-slate-800 text-slate-100 rounded-3xl w-full max-w-6xl h-[92vh] max-h-[900px] flex flex-col shadow-2xl overflow-hidden font-sans">
        {/* Modal Header */}
        <div className="bg-slate-950/80 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 shadow-inner">
              <ShieldAlert size={22} className="animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-black text-white">{t('wd.title')}</h2>
                <span className="text-[11px] font-black px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">{t('wd.chip')}</span>
              </div>
              <p className="text-xs text-slate-400">{t('wd.subtitle')}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Quick Status Pill */}
            <div
              data-testid="watchdog-status"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold ${
                p.status === 'healthy'
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : p.status === 'warning' || p.status === 'unknown'
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                    : 'bg-red-500/10 border-red-500/30 text-red-400'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${p.status === 'healthy' ? 'bg-emerald-400 animate-ping' : p.status === 'warning' || p.status === 'unknown' ? 'bg-amber-400' : 'bg-red-400 animate-pulse'}`} />
              <span>{t(`wd.status_${p.status}`)}</span>
            </div>

            <button type="button" onClick={p.onClose} className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors" data-testid="watchdog-close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Watchdog Live Health Strip */}
        <div className="bg-slate-950/40 border-b border-slate-800/80 px-6 py-2.5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-xs">
          {p.tiles.map((tile, i) => (
            <div key={tile.labelKey} className="flex items-center gap-2 bg-slate-900/60 px-3 py-1.5 rounded-xl border border-slate-800">
              {tileIcon(i, tile.tone)}
              <div className="truncate">
                <div className="text-[10px] text-slate-400">{t(tile.labelKey)}</div>
                <div className="font-mono font-bold text-slate-200 truncate">{tile.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="bg-slate-950/60 px-6 pt-3 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => p.onTab('stream')}
              className={`px-4 py-2.5 rounded-t-2xl font-black text-xs flex items-center gap-2 transition-all ${p.tab === 'stream' ? 'bg-slate-900 text-white border-t border-x border-slate-800 shadow-lg' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50'}`}
            >
              <Terminal size={16} />
              <span>{t('wd.tab_stream')}</span>
              <span className="px-2 py-0.5 rounded-full bg-slate-800 text-[10px] text-slate-300 font-mono">{filtered.length}</span>
            </button>
            <button
              type="button"
              onClick={() => p.onTab('diagnostics')}
              className={`px-4 py-2.5 rounded-t-2xl font-black text-xs flex items-center gap-2 transition-all ${p.tab === 'diagnostics' ? 'bg-slate-900 text-white border-t border-x border-slate-800 shadow-lg' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/50'}`}
            >
              <Zap size={16} />
              <span>{t('wd.tab_checks')}</span>
            </button>
          </div>
          {p.tab === 'stream' && (
            <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 mb-2">
              <button type="button" onClick={() => p.showCleared && p.onToggleCleared()} className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${!p.showCleared ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                {t('wd.view_open')}
              </button>
              <button type="button" onClick={() => !p.showCleared && p.onToggleCleared()} className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${p.showCleared ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                {t('wd.view_all')}
              </button>
            </div>
          )}
        </div>

        {/* Tab 1: entries */}
        {p.tab === 'stream' && (
          <div className="flex-1 flex flex-col min-h-0 bg-slate-900">
            <div className="p-4 border-b border-slate-800/80 bg-slate-950/30 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={p.search}
                  onChange={(e) => p.onSearch(e.target.value)}
                  placeholder={t('wd.search')}
                  className="w-full bg-slate-900 border border-slate-700/80 rounded-xl pr-9 pl-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                />
                {p.search && (
                  <button type="button" onClick={() => p.onSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 text-xs">
                    ✕
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-400 font-bold">{t('wd.level')}</span>
                <select value={p.level} onChange={(e) => p.onLevel(e.target.value as 'ALL' | WatchdogSeverity)} className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500">
                  <option value="ALL">{t('wd.level_all')}</option>
                  <option value="critical">CRITICAL</option>
                  <option value="warning">WARN</option>
                  <option value="info">INFO</option>
                </select>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-400 font-bold">{t('wd.module')}</span>
                <select value={p.module} onChange={(e) => p.onModule(e.target.value)} className="bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 max-w-[150px]">
                  <option value="ALL">{t('wd.module_all')}</option>
                  {modules.map((m) => (
                    <option key={m} value={m}>
                      {t(`wd.check_${m}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={p.onToggleLive}
                  className={`px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 border transition-all ${p.live ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-slate-800 border-slate-700 text-slate-400'}`}
                >
                  {p.live ? <Pause size={14} /> : <Play size={14} />}
                  <span>{t(p.live ? 'wd.live' : 'wd.paused')}</span>
                </button>
                <button type="button" onClick={p.onRefresh} className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors" title={t('wd.refresh')} data-testid="watchdog-refresh">
                  <RefreshCw size={14} className={p.refreshing ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 font-mono text-xs select-text">
              {filtered.length === 0 ? (
                <div className="h-64 flex flex-col items-center justify-center text-slate-500 space-y-2">
                  <Terminal size={36} className="text-slate-600" />
                  <p className="text-sm font-sans font-bold">{t(p.entries.length === 0 ? 'wd.empty' : 'wd.no_match')}</p>
                </div>
              ) : (
                filtered.map((e) => {
                  const isExpanded = expanded === e.id;
                  return (
                    <div
                      key={e.id}
                      onClick={() => setExpanded(isExpanded ? null : e.id)}
                      data-testid="watchdog-entry"
                      className={`rounded-2xl border transition-all cursor-pointer ${
                        e.cleared
                          ? 'bg-slate-950/40 border-slate-800 hover:bg-slate-950/70 opacity-60'
                          : e.severity === 'critical'
                            ? 'bg-red-950/40 border-red-800/80 hover:bg-red-950/60'
                            : e.severity === 'warning'
                              ? 'bg-amber-950/20 border-amber-800/40 hover:bg-amber-950/40'
                              : 'bg-slate-950/40 border-slate-800 hover:bg-slate-950/70'
                      } p-3.5`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap flex-1">
                          <span className="text-slate-500 text-[11px] select-all">{e.time}</span>
                          {levelBadge(e.severity)}
                          <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-purple-950 border border-purple-800 text-purple-300">WATCHDOG</span>
                          <span className="px-2 py-0.5 text-[10px] font-black rounded bg-slate-800 text-indigo-300 border border-slate-700">{t(e.checkLabelKey)}</span>
                          {e.cleared && <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-950 text-emerald-300 border border-emerald-800">{t('wd.cleared')}</span>}
                        </div>
                        <span className="text-[10px] text-slate-500 font-sans hover:text-slate-300">{t(isExpanded ? 'wd.hide_details' : 'wd.show_details')}</span>
                      </div>
                      <div className="mt-2 text-slate-200 font-sans font-medium text-xs leading-relaxed break-words">{e.message}</div>
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-slate-800/80 space-y-2 text-[11px]">
                          <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 text-slate-300">
                            <div className="font-bold text-indigo-400 mb-1">{t('wd.details')}</div>
                            <pre className="text-[10px] text-slate-400 whitespace-pre-wrap overflow-x-auto ltr text-left font-mono p-2 bg-black/30 rounded-lg" dir="ltr">
                              {e.details}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Tab 2: the server checks */}
        {p.tab === 'diagnostics' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-900">
            <div className="bg-slate-950 border border-slate-800 rounded-3xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
              <div className="space-y-2">
                <h3 className="text-base font-black text-white flex items-center gap-2">
                  <Zap size={20} className="text-amber-400" />
                  {t('wd.checks_title')}
                </h3>
                <p className="text-xs text-slate-400 max-w-xl leading-relaxed">{t('wd.checks_hint')}</p>
              </div>
              <button
                type="button"
                onClick={p.onRefresh}
                disabled={p.refreshing}
                className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-900 text-white font-black text-xs px-6 py-3.5 rounded-2xl flex items-center gap-2 transition-all shadow-lg shadow-indigo-600/30 whitespace-nowrap"
              >
                <RefreshCw size={16} className={p.refreshing ? 'animate-spin' : ''} />
                <span>{t(p.refreshing ? 'wd.refreshing' : 'wd.refresh_checks')}</span>
              </button>
            </div>
            <div className="bg-slate-950 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <h4 className="text-sm font-black text-white">{t('wd.checks_results')}</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {p.checks.map((c) => {
                  const st = c.outcome === 'clean' ? 'ok' : c.outcome === 'findings' ? 'warn' : c.outcome === 'error' ? 'fail' : 'warn';
                  return (
                    <div
                      key={c.name}
                      data-testid={`watchdog-check-${c.name}`}
                      className={`p-4 rounded-2xl border flex items-start gap-3 ${st === 'ok' ? 'bg-emerald-950/20 border-emerald-800/40 text-emerald-300' : st === 'warn' ? 'bg-amber-950/20 border-amber-800/40 text-amber-300' : 'bg-rose-950/30 border-rose-800/60 text-rose-300'}`}
                    >
                      <div className="mt-0.5">
                        {st === 'ok' ? <CheckCircle2 size={18} className="text-emerald-400" /> : st === 'warn' ? <AlertTriangle size={18} className="text-amber-400" /> : <AlertCircle size={18} className="text-rose-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-bold text-xs text-white">{t(c.labelKey)}</div>
                          {c.lastRun && <span className="font-mono text-[10px] bg-black/40 px-2 py-0.5 rounded text-slate-400">{c.lastRun}</span>}
                        </div>
                        <div className="text-xs mt-1 text-slate-300 leading-relaxed">
                          {c.outcome === null ? t('wd.check_never') : c.outcome === 'error' ? t('wd.check_error', { error: c.error ?? '' }) : c.outcome === 'clean' ? t('wd.check_clean') : t('wd.check_findings', { n: String(c.findings) })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
